import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import { formatPrice } from "@/lib/currency";
import DatePicker from "@/components/DatePicker";
import { AdminVerifiedBadge } from "@/components/AdminVerifiedBadge";
import { toast } from "sonner";
import {
  Star, MapPin, Users, Check, Loader2, ArrowLeft, ShieldCheck, Calendar, Clock, Heart,
  Zap, Sparkles, CreditCard,
} from "lucide-react";

const TIME_SLOTS = ["08:00 AM", "10:00 AM", "12:00 PM", "01:00 PM", "03:00 PM", "05:00 PM"];

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [listing, setListing] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState(2);
  const [busy, setBusy] = useState(false);
  const [fav, setFav] = useState(false);
  const [defaultPm, setDefaultPm] = useState(null);
  const [cardLast4, setCardLast4] = useState(null);
  const [redeem, setRedeem] = useState(0);
  const [fullDates, setFullDates] = useState([]);
  const [searchParams] = useSearchParams();
  const [agentRef, setAgentRef] = useState("");

  useEffect(() => {
    const a = (searchParams.get("agent") || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    if (a) {
      localStorage.setItem("carivisor_agent_ref", a);
      setAgentRef(a);
    } else {
      setAgentRef(localStorage.getItem("carivisor_agent_ref") || "");
    }
  }, [searchParams]);

  useEffect(() => {
    if (!agentRef || !id || !listing?.affiliate_enabled) return;
    const key = `carivisor_clk_${id}_${agentRef}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    api.post("/agents/click", { tour_id: id, handle: agentRef }).catch(() => {});
  }, [agentRef, id, listing]);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => {
      setListing(r.data);
      setGuests(Math.min(2, r.data.max_guests));
    }).catch(() => navigate("/browse"));
    api.get(`/listings/${id}/availability`).then((r) => setFullDates(r.data.full_dates || [])).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/payments/methods").then((r) => {
        setDefaultPm(r.data.default);
        const dm = r.data.methods.find((m) => m.id === r.data.default) || r.data.methods[0];
        setCardLast4(dm ? dm.last4 : null);
      }).catch(() => {});
    }
  }, [user]);

  const isFlat = listing?.price_type === "flat";
  const subtotal = listing ? (isFlat ? listing.price : listing.price * guests) : 0;
  const points = user?.points || 0;
  const maxRedeemPts = Math.min(points, Math.floor(subtotal * 0.5) * 100);
  const discount = Math.min(redeem / 100, subtotal * 0.5);
  const total = Math.max(0, subtotal - discount);

  const toggleFav = async () => {
    if (!user) return promptAuth();
    try {
      if (fav) { await api.delete(`/favorites/${id}`); setFav(false); toast("Removed from favorites"); }
      else { await api.post(`/favorites/${id}`); setFav(true); toast.success("Saved to favorites"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const promptAuth = () => {
    toast("Log in to book this experience");
    navigate(`/login?return=${encodeURIComponent(`/listing/${id}`)}`);
  };

  const validate = () => {
    if (!date) { toast.error("Please select a date"); return false; }
    if (!time) { toast.error("Please select a time"); return false; }
    if (guests > listing.max_guests) { toast.error(`Max ${listing.max_guests} travelers`); return false; }
    return true;
  };

  const fastBook = async () => {
    if (!user) return promptAuth();
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/fast", {
        listing_id: id, date, time, guests, points_to_redeem: Math.round(redeem),
        agent_ref: agentRef || "",
      });
      await refreshUser();
      toast.success("Booked in one tap! 🎉");
      navigate(`/booking/${data.id}/confirmation`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const checkout = async () => {
    if (!user) return promptAuth();
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        listing_id: id, date, time, guests,
        points_to_redeem: Math.round(redeem), origin_url: window.location.origin,
        agent_ref: agentRef || "",
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070A18]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070A18]">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-8 pb-16">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-orange-400">{listing.category}</span>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-white mt-2 max-w-2xl">{listing.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-300">
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#22E67A] text-[#22E67A]" /> <span className="rating-green font-bold">{listing.rating?.toFixed(1)}</span> ({listing.reviews_count})</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-orange-400" /> {listing.duration}</span>
              <span className="flex items-center gap-1"><Users className="w-4 h-4 text-fuchsia-400" /> Up to {listing.max_guests}</span>
            </div>
            <div className="mt-3">
              <span data-testid="cancellation-badge"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500/25 to-fuchsia-500/25 border border-orange-400/40 text-orange-200 animate-pulse">
                <ShieldCheck className="w-3.5 h-3.5" /> Free cancellation up to {listing.cancellation_window || 24}h before — full refund
              </span>
            </div>
          </div>
          <button onClick={toggleFav} data-testid="detail-fav-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full glass text-slate-200 hover:text-[#FF4D4D] transition-colors">
            <Heart className={`w-4 h-4 ${fav ? "fill-[#FF4D4D] text-[#FF4D4D]" : ""}`} /> Save
          </button>
        </div>

        {/* Gallery */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <div className="lg:col-span-2 rounded-2xl overflow-hidden aspect-[16/10] glass">
            <img src={listing.images?.[activeImg]} alt={listing.title} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-4">
            {listing.images?.slice(0, 3).map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)}
                className={`rounded-2xl overflow-hidden aspect-video lg:aspect-auto lg:h-full ${activeImg === i ? "neon-border" : "border border-[#232A4D]"}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
          {/* Left */}
          <div className="lg:col-span-2 space-y-10">
            {/* Quick facts */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="glass rounded-xl p-4">
                <Clock className="w-5 h-5 text-orange-400 mb-2" />
                <p className="text-xs text-slate-400">Duration</p>
                <p className="font-heading font-semibold text-white" data-testid="detail-duration">{listing.duration}</p>
              </div>
              <div className="glass rounded-xl p-4">
                <MapPin className="w-5 h-5 text-cyan-300 mb-2" />
                <p className="text-xs text-slate-400">Meeting point</p>
                <p className="font-heading font-semibold text-white text-sm" data-testid="detail-meeting-point">{listing.meeting_point}</p>
              </div>
              <div className="glass rounded-xl p-4">
                <Users className="w-5 h-5 text-fuchsia-400 mb-2" />
                <p className="text-xs text-slate-400">Group size</p>
                <p className="font-heading font-semibold text-white">Up to {listing.max_guests}</p>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full overflow-hidden btn-neon flex items-center justify-center font-heading font-bold shrink-0">
                  {listing.host?.avatar_url
                    ? <img src={fileUrl(listing.host.avatar_url)} alt={listing.provider_name} className="w-full h-full object-cover" data-testid="host-avatar" />
                    : (listing.provider_name?.[0])}
                </div>
                <div>
                  <p className="text-xs text-slate-400">Operated by</p>
                  <p className="font-heading font-semibold text-white flex items-center gap-1.5">
                    {listing.host?.name || listing.provider_name}
                    {listing.host?.admin_verified && <AdminVerifiedBadge showLabel={false} />}
                  </p>
                </div>
              </div>
              {listing.host?.bio && <p className="text-slate-300 leading-relaxed mb-3" data-testid="host-bio">{listing.host.bio}</p>}
              <p className="text-slate-300 leading-relaxed">{listing.description}</p>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold text-white mb-4">What's included</h3>
              <div className="grid grid-cols-2 gap-3">
                {listing.amenities?.map((a) => (
                  <div key={a} className="flex items-center gap-2 text-sm text-slate-300 glass rounded-xl px-4 py-3">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {a}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold text-white mb-4">Meeting point</h3>
              <div className="glass rounded-2xl h-56 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 gradient-hero opacity-60" />
                <div className="relative text-center">
                  <MapPin className="w-8 h-8 text-cyan-300 mx-auto mb-2 floaty" />
                  <p className="text-white font-medium">{listing.meeting_point}</p>
                  <p className="text-slate-400 text-sm">{listing.island}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Booking widget */}
          <div className="lg:col-span-1">
            <div className="glass neon-border rounded-2xl p-6 sticky top-24" data-testid="booking-widget">
              <div className="flex items-baseline gap-1 mb-5">
                <span className="font-heading text-3xl font-extrabold price-red">{formatPrice(listing.price, listing.currency)}</span>
                <span className="text-slate-400 text-sm">{isFlat ? "flat rate" : "per person"}</span>
              </div>

              {agentRef && listing?.affiliate_enabled && (
                <div data-testid="agent-referral-badge" className="mb-3 flex items-center gap-2 rounded-lg bg-fuchsia-500/10 border border-fuchsia-400/25 px-3 py-2 text-xs text-fuchsia-200">
                  <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" /> Referred by agent <span className="font-semibold text-fuchsia-100">@{agentRef}</span>
                </div>
              )}

              <div className="mb-3">
                <label className="text-xs text-slate-400 flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> Select date</label>
                <DatePicker testid="booking-date" value={date} min={new Date().toISOString().split("T")[0]}
                  disabledDates={fullDates}
                  placeholder="Choose your date" onChange={setDate} />
              </div>

              <div className="mb-3">
                <label className="text-xs text-slate-400 flex items-center gap-1 mb-1"><Clock className="w-3 h-3" /> Select time</label>
                <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                  {TIME_SLOTS.map((t) => (
                    <button key={t} type="button" data-testid={`time-${t}`}
                      onClick={() => setTime(t)}
                      className={`text-xs rounded-lg py-2 border transition-colors ${
                        time === t ? "btn-neon border-transparent font-semibold" : "bg-[#0B0E1B] border-[#232A4D] text-slate-300 hover:border-cyan-400/40"
                      }`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs text-slate-400 flex items-center gap-1 mb-1"><Users className="w-3 h-3" /> Travelers</label>
                <input type="number" data-testid="booking-guests" value={guests} min={1} max={listing.max_guests}
                  onChange={(e) => setGuests(Number(e.target.value))}
                  className="w-full bg-[#0B0E1B] border border-[#232A4D] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
              </div>

              {/* Points redemption */}
              {user && subtotal > 0 && maxRedeemPts >= 100 && (
                <div className="mb-4 glass rounded-xl p-4" data-testid="points-redeem">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-cyan-300 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Pay with points</span>
                    <span className="text-xs text-slate-400">{points.toLocaleString()} available</span>
                  </div>
                  <input type="range" min={0} max={maxRedeemPts} step={100} value={redeem}
                    data-testid="points-slider"
                    onChange={(e) => setRedeem(Number(e.target.value))}
                    className="w-full accent-cyan-400" />
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-slate-400">{redeem.toLocaleString()} pts</span>
                    <span className="text-emerald-400 font-medium">−{formatPrice(discount, listing.currency)}</span>
                  </div>
                </div>
              )}

              {subtotal > 0 && (
                <div className="space-y-2 mb-5 text-sm border-t border-[#232A4D] pt-4">
                  <div className="flex justify-between text-slate-300">
                    <span>{formatPrice(listing.price, listing.currency)} {isFlat ? "flat" : `× ${guests} travelers`}</span>
                    <span data-testid="booking-subtotal">{formatPrice(subtotal, listing.currency)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-cyan-300">
                      <span>Points discount</span>
                      <span>-{formatPrice(discount, listing.currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-heading font-bold text-white text-base border-t border-[#232A4D] pt-3">
                    <span>Total</span>
                    <span data-testid="booking-total" className="price-red">{formatPrice(total, listing.currency)}</span>
                  </div>
                </div>
              )}

              {/* CTA */}
              {!user ? (
                <button onClick={promptAuth} data-testid="booking-submit-btn"
                  className="w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2">
                  Book Now
                </button>
              ) : cardLast4 ? (
                <>
                  <button onClick={fastBook} disabled={busy} data-testid="one-tap-book-btn"
                    className="w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</> : <><Zap className="w-4 h-4" /> One-tap book · •••• {cardLast4}</>}
                  </button>
                  <button onClick={checkout} disabled={busy} data-testid="booking-submit-btn"
                    className="w-full mt-2 rounded-xl py-2.5 text-sm text-slate-400 hover:text-cyan-300 flex items-center justify-center gap-1.5">
                    <CreditCard className="w-4 h-4" /> Pay another way
                  </button>
                </>
              ) : (
                <>
                  <button onClick={checkout} disabled={busy} data-testid="booking-submit-btn"
                    className="w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : "Book Now"}
                  </button>
                  <p className="text-xs text-slate-500 text-center mt-2">
                    <Link to="/dashboard" className="text-cyan-300 hover:underline">Add a card</Link> for one-tap booking
                  </p>
                </>
              )}

              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Secure checkout by Stripe
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
